import type { DemoDoc, Step } from '@mado/schema';
import {
  presenterBubble,
  resolvePresenterSettings,
  type PresenterSettings,
  type PxRect,
} from '@mado/schema/presenter';
import { resolveAssetUrl } from './media';
import { prefersReducedMotion } from './motion';
import { el } from './stage';

/**
 * The presenter (webcam) bubble — Watch mode and exported video only, never
 * click-through (CLAUDE.md, founder decision). A muted, autoplaying, looped-
 * off `<video>` cropped to a circle or rounded square in one corner of the
 * stage, synced to start with the step's narration (`watch.ts` calls `show`
 * at the same point it calls `playNarration`).
 *
 * A document without `videoAssets` (every demo published before record mode,
 * and most made with it) must cost nothing: `active` is false and every other
 * method is a no-op, so the runtime can call this unconditionally without a
 * branch at every call site.
 */
export class Presenter {
  readonly root: HTMLElement;
  private readonly settings: PresenterSettings;
  private readonly enabled: boolean;
  private video: HTMLVideoElement | null = null;
  private currentUrl: string | null = null;
  private shown = false;

  constructor(
    private readonly doc: DemoDoc,
    layer: HTMLElement,
    private readonly assetBase: string | null | undefined,
  ) {
    this.settings = resolvePresenterSettings(doc.theme.presenter);
    this.enabled = this.settings.show && !!doc.videoAssets && !!Object.keys(doc.videoAssets).length;
    this.root = el('div', 'mado-presenter');
    this.root.setAttribute('aria-hidden', 'true');
    layer.appendChild(this.root);
  }

  /** Whether this document has any presenter clip to ever show. Callers use
   *  this to skip avoid-rect work entirely on the common document. */
  get active(): boolean {
    return this.enabled;
  }

  private clipUrl(step: Step): string | null {
    if (!this.enabled) return null;
    const id = step.presenter?.clip;
    const asset = id ? this.doc.videoAssets?.[id] : undefined;
    return asset ? resolveAssetUrl(asset.mp4, this.assetBase) : null;
  }

  /**
   * The step's clip duration if it will actually show (the feature is on,
   * the step has a clip), else `null`. Pure — mirrors `WatchAudio.narrationMs`
   * so `watch.ts` can size a step's dwell off the longer of the two without
   * caring which one produced the floor.
   */
  durationMs(step: Step): number | null {
    if (!this.enabled) return null;
    const id = step.presenter?.clip;
    const asset = id ? this.doc.videoAssets?.[id] : undefined;
    return asset ? asset.durationMs : null;
  }

  /** Start playing this step's clip, fading the bubble in. No-op (and hides
   *  any clip already showing) when the step has none. */
  show(step: Step): void {
    const url = this.clipUrl(step);
    if (!url) return this.hide();
    if (!this.video) {
      const v = document.createElement('video');
      v.className = 'mado-presenter-video';
      v.muted = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.disableRemotePlayback = true;
      this.video = v;
      this.root.appendChild(v);
    }
    if (this.currentUrl !== url) {
      this.currentUrl = url;
      this.video.src = url;
    } else {
      this.video.currentTime = 0;
    }
    // `Promise.resolve(...)`, not a bare `.catch()`: jsdom's `play()` returns
    // `undefined` rather than a promise (mixer.ts's element fallback hits the
    // same thing and wraps it the same way). A real browser's own rejection
    // (autoplay blocked, network) is just as harmless to swallow — the bubble
    // then shows its last decoded frame, or nothing; narration and the visual
    // choreography are unaffected either way.
    void Promise.resolve(this.video.play()).catch(() => {});
    const reduced = prefersReducedMotion();
    this.root.classList.toggle('mado-presenter--reduced', reduced);
    if (!this.shown) {
      this.shown = true;
      this.root.classList.add('mado-presenter--in');
    }
  }

  /** Fade the bubble out and stop the clip. Safe to call when already hidden. */
  hide(): void {
    if (this.shown) {
      this.shown = false;
      this.root.classList.remove('mado-presenter--in');
    }
    this.video?.pause();
  }

  /**
   * Position and shape the bubble for the current stage size, keeping clear
   * of `avoid` (the chrome bar's band, the step's own target/tooltip, a
   * story's caption band) where a corner exists that manages it — see
   * `@mado/schema/presenter` `presenterBubble`. Safe to call every render,
   * shown or not, so the bubble is already in place the instant it fades in.
   */
  layout(stage: { w: number; h: number }, avoid: readonly PxRect[]): void {
    if (!this.enabled) return;
    const bubble = presenterBubble(stage, this.settings, avoid);
    const s = this.root.style;
    s.width = `${bubble.rect.w}px`;
    s.height = `${bubble.rect.h}px`;
    s.left = `${bubble.rect.x}px`;
    s.top = `${bubble.rect.y}px`;
    this.root.classList.toggle('mado-presenter--rounded', bubble.shape === 'rounded');
  }

  /** Warm the next step's clip (like `Prefetcher` does for images) so it
   *  starts the instant it is shown rather than waiting on the network. */
  prefetch(step: Step): void {
    const url = this.clipUrl(step);
    if (!url || url === this.currentUrl) return;
    const v = document.createElement('video');
    v.preload = 'auto';
    v.muted = true;
    v.src = url;
  }

  destroy(): void {
    this.video?.pause();
    this.root.remove();
  }
}
